import React, { useState, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { FaIdCard, FaBuilding, FaBriefcase, FaMapMarkerAlt, FaCalendarAlt, FaPhone, FaEnvelope, FaFileUpload, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { saveEmploymentDetails } from '../../../services/candidate/api';
import PopupModel from '../../popup-model/PopupModel';
import './NewEmployement.css';
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TextField from "@mui/material/TextField";



const EmployeeDetailsForm = ({ employmentId }) => {
  const [formData, setFormData] = useState({
    employeeCode: '',
    companyName: '',
    jobTitle: '',
    location: '',
    workingFrom: '',
    workingTo: '',
    hrContactNumber: '',
    hrContactEmail: '',
    employerReferenceNumber: '',
    uanNumber: '',
  });

  const navigate = useNavigate();

  const [files, setFiles] = useState({
    experienceLetter: null,
    offerLetter: null,
    salarySlips: [],
    bankStatement: null,
    form16: null,
    form26: null,
  });

  const [loading, setLoading] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [showResultPopup, setShowResultPopup] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const inputRefs = useRef({});

  // Helper: Generate unique key from file (name + size)
  const getFileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;

  // Check if file already exists
  const isFileDuplicate = (newFile, existingFiles) => {
    const newKey = getFileKey(newFile);
    if (Array.isArray(existingFiles)) {
      return existingFiles.some((f) => getFileKey(f) === newKey);
    }
    return existingFiles && getFileKey(existingFiles) === newKey;
  };

  const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    try {
      if (dateStr.match(/^\d{4}$/)) {
        return new Date(parseInt(dateStr, 10), 0, 1);
      } else if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) {
        const [day, month, year] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      return null;
    } catch {
      return null;
    }
  };

  const handleKeyDown = (e, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const fieldOrder = [
        'employeeCode',
        'companyName',
        'jobTitle',
        'location',
        'workingFrom',
        'workingTo',
        'hrContactNumber',
        'hrContactEmail',
        'employerReferenceNumber',
        'uanNumber',
      ];

      const currentIndex = fieldOrder.indexOf(fieldName);
      if (currentIndex < fieldOrder.length - 1) {
        const nextField = fieldOrder[currentIndex + 1];
        inputRefs.current[nextField]?.focus();
      }
    }
  };

  const handleDrop = (e, field) => {
    e.preventDefault();
    handleFileUpload(Array.from(e.dataTransfer.files), field);
  };

  const handleFileChange = (e, field) => {
    handleFileUpload(Array.from(e.target.files), field);
    e.target.value = ''; // Reset input to allow re-selecting same file
  };

  // Unified file upload handler with duplicate detection
  const handleFileUpload = (uploadedFiles, field) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const validFiles = [];
    const duplicates = [];
    const invalidFiles = [];

    uploadedFiles.forEach((file) => {
      if (file.type !== 'application/pdf') {
        invalidFiles.push(file.name);
      } else if (file.size > maxSize || file.size === 0) {
        invalidFiles.push(file.name);
      } else if (isFileDuplicate(file, files[field])) {
        duplicates.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    // Set error messages
    let errorMsg = '';
    if (duplicates.length > 0) {
      errorMsg = `Duplicate file(s) ignored: ${duplicates.join(', ')}`;
    } else if (invalidFiles.length > 0) {
      errorMsg = `Invalid file(s) rejected: ${invalidFiles.join(', ')} (only PDFs ≤5MB allowed)`;
    }

    setValidationErrors((prev) => ({
      ...prev,
      [field]: errorMsg || '',
    }));

    // Add valid non-duplicate files
    if (validFiles.length > 0) {
      setFiles((prev) => {
        if (field === 'salarySlips') {
          const newSlips = [...prev.salarySlips, ...validFiles].slice(0, 3);
          return { ...prev, salarySlips: newSlips };
        }
        // For single file fields, replace if new one is valid
        return { ...prev, [field]: validFiles[0] };
      });
    }
  };

  // Fixed remove handler with functional update
  const handleRemoveFile = (field, index = null) => {
    setFiles((prev) => {
      if (field === 'salarySlips') {
        return {
          ...prev,
          salarySlips: prev.salarySlips.filter((_, i) => i !== index),
        };
      }
      return { ...prev, [field]: null };
    });

    setValidationErrors((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

const handleInputChange = (e) => {
  const { name, value } = e.target;

  if (name === 'hrContactEmail') {
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
    return;
  }

  const capitalizedValue =
    value.length > 0
      ? value.charAt(0).toUpperCase() + value.slice(1)
      : value;

  setFormData((prev) => ({ ...prev, [name]: capitalizedValue }));
  validateField(name, capitalizedValue);
};

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const validateField = (name, value) => {
    // ... (your existing validation logic remains unchanged)
    let error = '';
    switch (name) {
      case 'employeeCode':
        if (!value) error = 'Employee code is required';
        else if (!/^[a-zA-Z0-9 ]+$/.test(value)) error = 'Special characters are not allowed';
        break;
      case 'companyName':
        if (!value) error = 'Company name is required';
        else if (!/^[a-zA-Z0-9 ]+$/.test(value)) error = 'Special characters are not allowed';
        break;
      case 'jobTitle':
        if (!value) error = 'Job title is required';
        else if (!/^[A-Za-z ]{1,50}$/.test(value)) error = 'Job title can only contain alphabets and spaces (max 50 characters)';
        break;
      case 'location':
        if (value && !/^[A-Za-z ,]{1,100}$/.test(value)) {
          error = 'Location can only contain alphabets, spaces, and commas (max 100 characters)';
        }
        break;
      case 'workingFrom':
        if (!value) error = 'Working from date is required';
        else if (!/^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4}$/.test(value)) error = 'Working from must be in DD-MM-YYYY format';
        else {
          const fromDate = parseDateString(value);
          const toDate = parseDateString(formData.workingTo);
          const today = new Date();
          if (fromDate && fromDate > today) error = 'Working From date cannot be in the future.';
          if (fromDate && toDate && fromDate > toDate) error = 'Working From date cannot be after Working To date.';
        }
        break;
      case 'workingTo':
        if (!value) error = 'Working to date is required';
        else if (!/^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4}$/.test(value)) error = 'Working to must be in DD-MM-YYYY';
        else {
          const fromDate = parseDateString(formData.workingFrom);
          const toDate = parseDateString(value);
          const today = new Date();
          if (toDate && toDate > today) error = 'Working To date cannot be in the future.';
          if (fromDate && toDate && fromDate > toDate) error = 'Working From date cannot be after Working To date.';
        }
        break;
      case 'hrContactNumber':
        if (value && !/^\d{10}$/.test(value)) error = 'HR contact number must be 10 digits if provided';
        break;
      case 'hrContactEmail':
        if (!value) error = 'HR contact email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Valid HR contact email is required';
        else {
          const domain = value.split('@')[1].toLowerCase();
          const blocked = ["gmail.com", "gamil.com", "gmal.com", "gnail.com", "yahoo.com", "yhoo.com", "yaoo.com", "hotmail.com", "hotmial.com", "hotmal.com", "outlook.com", "outllok.com", "outlok.com", "reddit.com", "redit.com", "rddit.com"];
          if (blocked.includes(domain)) {
            error = 'Email providers like Gmail, Outlook, Yahoo, Reddit are not allowed. Use a company email address.';
          }
        }
        break;
      case 'uanNumber':
        if (value && !/^\d{12}$/.test(value)) error = 'UAN must be exactly 12 digits';
        break;
      case 'employerReferenceNumber':
        if (value && !/^\d{10}$/.test(value)) error = 'Employer Reference Number must be exactly 10 digits';
        break;
      default:
        break;
    }
    setValidationErrors((prev) => ({ ...prev, [name]: error }));
    return error;
  };

  const validateForm = () => {
    const errors = {};
    Object.keys(formData).forEach((key) => {
      const fieldError = validateField(key, formData[key]);
      if (fieldError) errors[key] = fieldError;
    });

    if (!files.offerLetter) errors.offerLetter = 'Offer letter is required';
    if (!files.bankStatement) errors.bankStatement = 'Bank statement is required';
    if (files.salarySlips.length !== 3) errors.salarySlips = 'Exactly 3 salary slips are required';

    setValidationErrors((prev) => ({ ...prev, ...errors }));
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmissionResult(null);
    setShowResultPopup(false);

    if (!validateForm()) {
      setSubmissionResult({
        isSuccess: false,
        title: 'Validation Error',
        message: 'Please fix the errors in the form before submitting.',
      });
      setShowResultPopup(true);
      return;
    }

    setLoading(true);
    const data = new FormData();
    Object.keys(formData).forEach((key) => data.append(key, formData[key]));

    if (files.experienceLetter) data.append('experienceLetter', files.experienceLetter);
    if (files.offerLetter) data.append('offerLetter', files.offerLetter);
    if (files.bankStatement) data.append('bankStatement', files.bankStatement);
    if (files.form16) data.append('form16', files.form16);
    if (files.form26) data.append('form26', files.form26);
    files.salarySlips.forEach((file) => data.append('salarySlips', file));

    try {
      const response = await saveEmploymentDetails(data);
      if (response.statusCode === 200 && response.status === true) {
        setSubmissionResult({
          isSuccess: true,
          title: 'Success',
          message: response.message || 'Employment details saved successfully!',
        });
        setFormData({
          employeeCode: '', companyName: '', jobTitle: '', location: '',
          workingFrom: '', workingTo: '', hrContactNumber: '', hrContactEmail: '',
          employerReferenceNumber: '', uanNumber: '',
        });
        setFiles({
          experienceLetter: null, offerLetter: null, salarySlips: [],
          bankStatement: null, form16: null, form26: null,
        });
        setValidationErrors({});
      } else {
        throw new Error(response.message || 'Failed to save employment details');
      }
    } catch (err) {
      let errorMessage = err.response?.data?.message || err.message || 'Failed to save employment details';
      if (err.response?.status === 503) errorMessage = 'The server is temporarily unavailable. Please try again later.';
      else if (err.response?.status === 400) errorMessage = 'Invalid data submitted. Please check your inputs.';
      else if (err.response?.status === 401) errorMessage = 'Unauthorized request. Please ensure you are logged in.';
      setSubmissionResult({
        isSuccess: false,
        title: 'Submission Error',
        message: errorMessage,
      });
    } finally {
      setLoading(false);
      setShowResultPopup(true);
    }
  };

  const closeResultPopup = () => {
    setShowResultPopup(false);
    setSubmissionResult(null);
  };

  const renderUploadField = (label, fieldName) => (
    <div className="uploadField">
      <p className="upload-label-title">
        <FaFileUpload className="filed-icons" /> {label}
      </p>
      <div
        className="upload-certificate-button"
        onClick={() => document.getElementById(fieldName).click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => handleDrop(e, fieldName)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            document.getElementById(fieldName).click();
          }
        }}
      >
        {fieldName === 'salarySlips'
          ? `Drag & Drop or Click (${3 - files.salarySlips.length}/3 PDFs remaining)`
          : 'Drag & Drop or Click to Upload PDF'}
      </div>
      <input
        type="file"
        id={fieldName}
        accept=".pdf"
        multiple={fieldName === 'salarySlips'}
        hidden
        onChange={(e) => handleFileChange(e, fieldName)}
      />
      <div className="upload-error-container">
        {validationErrors[fieldName] && (
          <Typography color="error" variant="caption">
            {validationErrors[fieldName]}
          </Typography>
        )}

        {/* Single file fields */}
        {fieldName !== 'salarySlips' && files[fieldName] && (
          <div className="file-list">
            <span className="file-list-item">
              {files[fieldName].name}
              <FaTimes
                className="remove-file-icon"
                onClick={() => handleRemoveFile(fieldName)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleRemoveFile(fieldName)}
                role="button"
                tabIndex={0}
                aria-label={`Remove ${files[fieldName].name}`}
                title="Remove file"
              />
            </span>
          </div>
        )}

        {/* Multiple salary slips */}
        {fieldName === 'salarySlips' && files.salarySlips.length > 0 && (
          <div className="file-list">
            {files.salarySlips.map((file, index) => (
              <span key={index} className="file-list-item">
                {file.name}
                <FaTimes
                  className="remove-file-icon"
                  onClick={() => handleRemoveFile('salarySlips', index)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleRemoveFile('salarySlips', index)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Remove ${file.name}`}
                  title="Remove file"
                />
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderInputField = (label, name, type = 'text', placeholder = '', Icon) => (
    <div className="inputField">
      <p className="upload-label-title">
        {Icon && <Icon className="filed-icons" />} {label}
      </p>
      <input
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={(e) => handleKeyDown(e, name)}
        placeholder={placeholder}
        className={validationErrors[name] ? 'input-error' : ''}
        ref={(el) => (inputRefs.current[name] = el)}
      />
      {validationErrors[name] && (
        <Typography color="error" variant="caption">
          {validationErrors[name]}
        </Typography>
      )}
    </div>
  );

  return (
    <>
      <h2 className="candidate-titles">Employment Details</h2>
      <Box className="employee-form-container">
        {loading && (
          <Box className="loading-overlay">
            <Box className="loading-content">
              <CircularProgress size={40} style={{ color: '#4D0054' }} />
              <Typography className="loading-text">Saving...</Typography>
            </Box>
          </Box>
        )}
        <form className="employee-form" onSubmit={handleSubmit}>
          <div className="grid-2col">
            {renderInputField('Employee Code*', 'employeeCode', 'text', '', FaIdCard)}
            {renderInputField('Company Name*', 'companyName', 'text', '', FaBuilding)}
            {renderInputField('Job Title*', 'jobTitle', 'text', '', FaBriefcase)}
            {renderInputField('Location (optional)', 'location', 'text', '', FaMapMarkerAlt)}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
<div className="inputField">
  <p className="upload-label-title">
    <FaCalendarAlt className="filed-icons" /> Working From*
  </p>

  <DatePicker
    views={["year", "month", "day"]} 
    format="DD-MM-YYYY"
    disableFuture
    showDaysOutsideCurrentMonth
    value={formData.workingFrom ? dayjs(formData.workingFrom, "DD-MM-YYYY") : null}
    onChange={(newValue) =>
      setFormData((prev) => ({
        ...prev,
        workingFrom: newValue ? newValue.format("DD-MM-YYYY") : "",
        workingTo: "",
      }))
    }
    slotProps={{
      textField: {
        variant: "standard",
        fullWidth: true,
        placeholder: "DD-MM-YYYY",
        error: !!validationErrors.workingFrom,
        helperText: validationErrors.workingFrom,
      },
    }}
  />
</div>

<div className="inputField">
  <p className="upload-label-title">
    <FaCalendarAlt className="filed-icons" /> Working To*
  </p>
 <DatePicker
    views={["year", "month", "day"]} 
    format="DD-MM-YYYY"
    disableFuture
    showDaysOutsideCurrentMonth
    minDate={
      formData.workingFrom
        ? dayjs(formData.workingFrom, "DD-MM-YYYY")
        : undefined
    }
    disabled={!formData.workingFrom}
    value={formData.workingTo ? dayjs(formData.workingTo, "DD-MM-YYYY") : null}
    onChange={(newValue) =>
      setFormData((prev) => ({
        ...prev,
        workingTo: newValue ? newValue.format("DD-MM-YYYY") : "",
      }))
    }
    slotProps={{
      textField: {
        variant: "standard",
        fullWidth: true,
        placeholder: "DD-MM-YYYY",
        error: !!validationErrors.workingTo,
        helperText: validationErrors.workingTo,
      },
    }}
  />
</div>
</LocalizationProvider>
            {renderInputField('HR Contact Number (optional)', 'hrContactNumber', 'tel', '', FaPhone)}
            {renderInputField('HR Contact Email*', 'hrContactEmail', 'email', '', FaEnvelope)}
            {renderInputField('Employer Reference / CoWorker Number (optional)', 'employerReferenceNumber', 'text', '', FaIdCard)}
            {renderInputField('Universal Account Number (UAN) (optional)', 'uanNumber', 'text', '', FaIdCard)}
          </div>

          <div className="grid-2col file-grid">
            {renderUploadField('Experience Letter (PDF Only) (optional)', 'experienceLetter')}
            {renderUploadField('Offer Letter (PDF Only)*', 'offerLetter')}
            {renderUploadField('Salary PaySlips (Exactly Last 3 months PDFs)*', 'salarySlips')}
            {renderUploadField('Bank Statement (Last 3 months PDF Only)*', 'bankStatement')}
            {renderUploadField('Form 16 (PDF Only) (optional)', 'form16')}
            {renderUploadField('Form 26 (PDF Only) (optional)', 'form26')}
          </div>

          <div className="emp-form-actions">
            <button className="backbutton" onClick={() => navigate('/candidate/employment-info')} type="button">
              Back
            </button>
            <button type="submit" className="stepHighSchoolButton" disabled={loading}>
              Save Details
            </button>
          </div>
        </form>

        {/* <PopupModel
          show={showResultPopup}
          onClose={closeResultPopup}
          title={submissionResult?.title}
          message={submissionResult?.message}
          isSuccess={submissionResult?.isSuccess}
        /> */}
        {showResultPopup && (
  <div className="newemp-popup-overlay">
    <div className="newemp-popup">
      <button
        className="popup-close-btn"
        onClick={closeResultPopup}
      >
        ×
      </button>

      {/* <div
        className={
          submissionResult?.isSuccess
            ? "popup-icon success"
            : "popup-icon error"
        }
      >
        {submissionResult?.isSuccess ? "✓" : "✕"}
      </div> */}

      <h3>{submissionResult?.title}</h3>

      <p>{submissionResult?.message}</p>

      <button
        className={
          submissionResult?.isSuccess
            ? "popup-ok-btn success"
            : "popup-ok-btn error"
        }
        onClick={closeResultPopup}
      >
        OK
      </button>
    </div>
  </div>
)}
      </Box>
    </>
  );
};

export default EmployeeDetailsForm;